import type React from 'react';

import { Button } from '@change/ds-core-ui/Button';
import { FacebookIcon } from '@change/ds-icons/FacebookIcon';
import { WhatsAppIcon } from '@change/ds-icons/WhatsAppIcon';

import { translationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core-react/i18n';

import type { ChannelAwareCtaChannel } from '#src/app/pages/psf/promoteOrShare/shared/hooks';
import { useResolvedLabel } from '#src/app/shared/hooks/i18n';

import { ACTION_BUTTON_TRACKING_DATA } from '../../constants';
import type { PetitionShareButtonConfig } from '../../types';

const CHANNEL_ICONS: Readonly<Record<ChannelAwareCtaChannel, React.ComponentType>> = {
	facebook: FacebookIcon,
	whatsapp: WhatsAppIcon,
};

type Props = {
	channel: ChannelAwareCtaChannel;
	buttonConfig: PetitionShareButtonConfig;
	onBeforeShare?: () => void;
};

/**
 * Yellow primary CTA matching the Copy link button (same DS `variant="primary"`), with no bespoke
 * colour — the `primary` variant supplies its own yellow background and dark label/icon. `size="small"`
 * matches the Copy link CTA (ShareChannelButton) so the stacked buttons are the same height.
 */
export const ChannelAwareCtaButton: React.FC<Props> = ({
	channel,
	buttonConfig: { ShareAction, label: channelLabel },
	onBeforeShare,
}) => {
	const { typedTranslate } = useI18n();
	const Icon = CHANNEL_ICONS[channel];
	// The channel's own localized short label (`...buttons.short.<channel>`), so a locale that renders a
	// brand name differently controls it rather than this component hardcoding the English form.
	const channelName = useResolvedLabel(channelLabel);

	// One shared, already-translated sentence for every channel and market, despite the key living under
	// `share.modals.heading`. A single key keeps the copy consistent across channels and the Top-8
	// locales rather than hardcoding the English form or splitting into per-channel keys.
	const label = typedTranslate(translationKey('corgi.psf.share.modals.heading.title'), {
		shareChannel: channelName,
	});

	return (
		<ShareAction onBeforeAction={onBeforeShare} trackingData={ACTION_BUTTON_TRACKING_DATA}>
			<Button
				variant="primary"
				size="small"
				leadingIcon={<Icon />}
				className="w-full"
				data-qa="petition-share-slide-up-channel-cta-button"
				data-testid="petition-share-slide-up-channel-cta-button"
			>
				{label}
			</Button>
		</ShareAction>
	);
};
